<template>
  <RouterLink
    :to="`/gw-completed/${gw.id}`"
    class="gw-completed-item v-centerer generic-border generic-border--clickable generic-border--card"
    :class="elClasses"
  >
    <div class="gw-completed-item__gw">
      <div class="top v-centerer">
        <span class="name">{{ gw.name }}</span>
      </div>
      <div>
        <span v-if="!elClasses['sm-portrait-size']" class="gw-completed-item__gw__duration">
          {{ duration }}
        </span>
        <span class="gw-completed-item__gw__ok-nok-ratio">
          <span class="ok">{{ nOKItems }}</span>
          <span>/</span>
          <span class="nok">{{ nNOKItems }}</span>
          <span>/</span>
          <span>{{ nNAItems }}</span>
        </span>
      </div>
    </div>

    <div class="gw-completed-item__date">
      <div>{{ date }}</div>
      <div>{{ dateTime }}</div>
    </div>

    <div v-if="!elClasses['sm-portrait-size']" class="gw-completed-item__leader h-v-centerer">
      <UserThumbnail
        v-if="gw.user"
        :value="gw.user"
        disabled
        fetch-data
        row
        show-name
      />
    </div>

    <div v-if="!elClasses['sm-portrait-size']" class="gw-completed-item__participants h-v-centerer">
      <div
        class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
        @click.prevent="openTeamRecapModal(gw.id)"
      >
        <Icon>{{ teamMedium }}</Icon>
      </div>
    </div>

    <div class="gw-completed-item__delete h-v-centerer">
      <div
        class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
        @click.prevent="deleteGW"
      >
        <Icon>{{ deleteMedium }}</Icon>
      </div>
    </div>
  </RouterLink>
</template>

<script>
/**
 * Component to display in a list of completed GW.
 * Display the name of the GW, the breakdown of OK/NOK/NA items, and the author.
 *
 * @vue-prop {Object} gw - Related GW
 */

import $ from 'jquery';
import toastr from 'toastr';
// Components
import Icon from '@/components/icon.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Utils
import { calert, secondsToArray, itemWidthClasses } from '@/utils/main';
import { deleteMedium, teamMedium } from '@/assets/icons';

export default {

  name: 'GwCompletedItem',

  components: {
    Icon,
    UserThumbnail,
  },

  props: {
    gw: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      deleteMedium,
      teamMedium,
      elClasses: {},
    };
  },

  computed: {
    duration() {
      if (!this.gw.duration) {
        return this.$t('message.GW_0_STEPS');
      }
      const time = secondsToArray(this.gw.duration);
      return `${time[0]}:${time[1]}:${time[2]}`;
    },
    /** String: Date to display as a string */
    date() {
      return this.elClasses['sm-portrait-size']
        ? this.$moment(this.gw.date).format(this.$t('minDateFormat'))
        : this.$moment(this.gw.date).format(this.$t('dateFormat'));
    },
    /** String: Time of the day display as a string */
    dateTime() {
      return this.$moment(this.gw.date).format('HH:mm');
    },
    /** Array: Concatenated checklist items of this GW */
    allChecklistItems() {
      return this.gw.steps.reduce((clItems, step) => {
        const cItems = step.checklists.reduce((itItems, checklist) => [...itItems, ...checklist.items], []);
        return [...clItems, ...cItems];
      }, []);
    },
    /** Number: Number of OK teams in all the checklist of this GW */
    nOKItems() {
      return this.allChecklistItems.filter(item => item.ok === 1).length;
    },
    /** Number: Number of OK tems in all the checklist of this GW */
    nNOKItems() {
      return this.allChecklistItems.filter(item => item.ok === 0).length;
    },
    /** Number: Number of OK tems in all the checklist of this GW */
    nNAItems() {
      return this.allChecklistItems.filter(item => item.ok === 2).length;
    },
  },

  created() {
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
  },

  mounted() {
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
  },

  methods: {
    openTeamRecapModal(gwId) {
      this.$http.post('/server/gembaWalks/getParticipants.php', { gwId })
        .then((response) => {
          const { data } = response;
          this.$emit('toggleModal', data);
        }).catch(() => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        });
    },
    deleteGW() {
      calert(this.$t('message.GW_CONFIRM_DELETE'), [
        {
          title: this.$t('message.DELETE'),
          sClass: 'red',
          action: () => {
            this.$emit('delete');
          },
        }, {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Style of the problem is determined by the size of $el. Apply classes */
    onSizeChanged() {
      const width = this.$el ? this.$el.offsetWidth : 0;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/gw-item.scss";

.gw-completed-item {
  display: flex;
  flex-flow: row nowrap;
  height: $main-tile-height;
  margin-top: 0.6rem;

  &__gw {
    flex: 1;
    min-width: 0px;
    margin-left: 0.7rem;
    line-height: 1.3rem;
    height: 2.6rem;
    font-size: 1rem;

    .name {
      font-weight: 600;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    &__ok-nok-ratio {
      margin-left: 0.5em;
      font-weight: 600;

      span {
        margin: 0 0.1em;
      }
      .ok {
        color: $color-green-good;
      }
      .nok {
        color: $color-red-bad;
      }
    }
  }

  &__leader {
    flex: 0 0 9.5rem;

    .user-thumbnail {

      &__label {
        font-style: italic;
        font-size: 0.9em;
      }

      &__avatar {
        transform: scale(0.9);
        margin-left: 0.5rem;
      }
    }
  }

  &__date {
    // Width = 6em + margins of 4rem
    flex: 0 0 10rem;
    text-align: center;
  }

  &__participants, &__delete {
    flex: 0 0 5.72rem;
  }

  &.sm-landscape-size {
    .gw-completed-item {
      &__date {
        flex: 0 0 7rem;
      }
      &__participants, &__delete {
        flex: 0 0 3.72rem;
      }
    }
  }

  &.sm-portrait-size {
    .gw-completed-item {
      &__gw {
        &__ok-nok-ratio {
          margin-left: 0;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          display: block;
        }
      }
      &__date {
        flex: 0 0 4.5rem;
      }
    }
  }
}
</style>
